import * as React from "react"
import compose from "recompose/compose"
import getContext from "recompose/getContext"
import withContext from "recompose/withContext"
import withHandlers from "recompose/withHandlers"
import withState from "recompose/withState"
import PropTypes from "prop-types"
import { addDays, isValid, isAfter } from "date-fns"
import { dateUrl, daysAgo, parseDateUrl, today } from "@/helpers/dates"
import { withTracking } from "@/helpers/hoc/withTracking"
import differenceInCalendarDays from "date-fns/differenceInCalendarDays"
import {
  addUrlProps,
  UrlQueryParamTypes,
  replaceInUrlQuery,
} from "react-url-query"

const validateDateRange = (date) => {
  const from = parseDateUrl(date.from)
  const to = parseDateUrl(date.to)
  const isValidFromDate = isValid(from) && !isAfter(from, new Date())
  const isValidToDate =
    isValid(to) && !isAfter(to, new Date()) && isAfter(to, from)

  const isValidDate = isValidFromDate && isValidToDate

  if (!isValidDate) {
    replaceInUrlQuery("dateRange", "")
  }

  return isValidDate
}

const urlPropsQueryConfig = {
  compare: {
    type: UrlQueryParamTypes.object,
    validate: validateDateRange,
  },
  dateRange: {
    type: UrlQueryParamTypes.object,
    validate: validateDateRange,
  },
  last: {
    type: UrlQueryParamTypes.string,
    validate(value) {
      const isValid = ["7", "30", "60", "90", "custom"].includes(value)

      if (!isValid) {
        replaceInUrlQuery("last", "")
      }

      return isValid
    },
  },
}

const DateRangeProvider = compose(
  addUrlProps({ urlPropsQueryConfig }),
  withTracking(),
  withState(
    "from",
    "setFrom",
    ({ dateRange }) =>
      (dateRange && parseDateUrl(dateRange.from)) || daysAgo(7),
  ),
  withState(
    "to",
    "setTo",
    ({ dateRange }) => (dateRange && parseDateUrl(dateRange.to)) || today(),
  ),
  withState("compare", "setCompare", ({ compare }) =>
    compare
      ? {
          from: parseDateUrl(compare.from),
          to: parseDateUrl(compare.to),
        }
      : null,
  ),
  withState(
    "last",
    "setLast",
    ({ dateRange, last }) => last || (dateRange && "custom") || "7",
  ),
  withHandlers({
    handleCompareChange: ({ onChangeCompare, setCompare }) => ({
      from,
      to,
    }) => {
      const compareDistance = differenceInCalendarDays(from, to)
      const compareTo = addDays(from, -1)
      const compareFrom = addDays(compareTo, compareDistance)

      setCompare({ from: compareFrom, to: compareTo })
      onChangeCompare({ from: dateUrl(compareFrom), to: dateUrl(compareTo) })
    },
  }),
  withHandlers({
    handleDateRangeChange: ({
      compare,
      handleCompareChange,
      onChangeDateRange,
      setFrom,
      setTo,
    }) => ({ from, to }) => {
      setFrom(from)
      setTo(to)
      compare && handleCompareChange({ from, to })

      onChangeDateRange({ from: dateUrl(from), to: dateUrl(to) })
    },
    handleLastChange: ({ onChangeLast, setLast, trackAction }) => (last) => {
      setLast(last)
      onChangeLast(last)
      trackAction("xri_date_range_chosen", {
        PropContextDimension2: `xri_${last}`,
      })
    },
    toggleCompare: ({
      compare,
      from,
      handleCompareChange,
      onChangeCompare,
      setCompare,
      to,
      trackAction,
      ...props
    }) => () => {
      if (compare) {
        setCompare(null)
        onChangeCompare(undefined)
      } else {
        handleCompareChange({ from, to })
        trackAction("xri_date_compare_previous_period_click")
      }
    },
  }),
  withContext(
    {
      compare: PropTypes.shape({
        from: PropTypes.instanceOf(Date),
        to: PropTypes.instanceOf(Date),
      }),
      from: PropTypes.instanceOf(Date),
      last: PropTypes.oneOf(["7", "30", "60", "90", "custom"]),
      setDateRange: PropTypes.func,
      setLast: PropTypes.func,
      to: PropTypes.instanceOf(Date),
      toggleCompare: PropTypes.func,
    },
    ({
      compare,
      from,
      handleDateRangeChange,
      handleLastChange,
      last,
      to,
      toggleCompare,
    }) => ({
      compare,
      from,
      last,
      setDateRange: handleDateRangeChange,
      setLast: handleLastChange,
      to,
      toggleCompare,
    }),
  ),
)((props) => React.Children.only(props.children))

export const withDateRange = compose(
  getContext({
    compare: PropTypes.shape({
      from: PropTypes.instanceOf(Date),
      to: PropTypes.instanceOf(Date),
    }),
    from: PropTypes.instanceOf(Date),
    last: PropTypes.oneOf(["7", "30", "60", "90", "custom"]),
    setDateRange: PropTypes.func,
    setLast: PropTypes.func,
    to: PropTypes.instanceOf(Date),
    toggleCompare: PropTypes.func,
  }),
)

export default DateRangeProvider
